"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { Typography } from "@repo/ui/aops-components/typography";
import { CourseLevelSection } from "@/app/components/CourseLevelSection";
import styles from "./LearningFormat.module.css";
import { useAtom } from "jotai";
import { learningFormatAtom, selectedCampusAtom, campusResolutionSettledAtom, selectedModalitySlugAtom } from "@/app/atoms/courseLevelSelections";
import { useHydrateAtoms } from "jotai/utils";
import { SelectableTile } from "../SelectableTile";
import { Accordion } from "@repo/ui/mantine";
import { Minus, Plus } from "lucide-react";
import { cn } from "@repo/ui/lib/utils";
import { LearningFormatMobile } from "./LearningFormatMobile";
import { FALLBACK_SUBTITLE, FALLBACK_TITLE, HIDE_DETAILS_KEY, LEARN_MORE_KEY, LEARN_MORE_VALUE, FALLBACK_LEARN_MORE_TITLE, FALLBACK_HIDE_DETAILS_TEXT, LearningFormatBestFor, LearningFormatDetailRow, LearningFormatHeader, LearningFormatQuickFact, LearningFormatWhyChoose, StickyFormatChrome, isBeastAcademyModality, useLinkToBAO, useModalityRows, type LearningFormatProps, STEP_TAG_KEY, FALLBACK_STEP_TAG, type Modality } from "./LearningFormatParts";
import { getKeyedCopyText } from "@repo/utils/keyedCopy";
import { getModalityIdBySlug } from "@/lib/learningFormatData";
import { resolvePreferredModalitySlug, shouldApplyUrlPreference } from "../courseLevelSelectionLogic";
import { getFormatAutoScrollTarget, scrollToCourseSection, shouldAutoScrollOnSelection } from "../scrollToCourseSection";
import { sendDiscoveryEventBeacon } from "@/lib/discoveryEventBeacon";
import { didOpenLearnMore, getSelectedModalitySlug } from "../LearningFormatSelectionUtils";
export type { LearningFormatProps } from "./LearningFormatParts";
const EMPTY_KEYED_COPY: {
  key: string;
  text: string;
}[] = [];

/** Stacked single-column layout below desktop multi-column (Figma tablet ≤800). */
const STACKED_LAYOUT_MQ = "(max-width: 1023px)";
function subscribeStackedLayout(onStoreChange: () => void) {
  const mql = window.matchMedia(STACKED_LAYOUT_MQ);
  mql.addEventListener("change", onStoreChange);
  return () => mql.removeEventListener("change", onStoreChange);
}
function getStackedLayoutSnapshot() {
  return window.matchMedia(STACKED_LAYOUT_MQ).matches;
}
function useIsStackedLearningFormat() {
  return useSyncExternalStore(subscribeStackedLayout, getStackedLayoutSnapshot, _temp);
}
function _temp() {
  return false;
}
type SelectLearningFormat = (modality: Modality) => void;

/**
 * Multi-column Learning Format (≥768px). Shared subgrid + one section-level
 * Learn more accordion that expands detail rows across all columns.
 */
function LearningFormatDesktop(t0) {
  const $ = _c(61);
  const {
    campuses,
    campusCoursePrices,
    courseLevel,
    coursePageSection,
    campusLocationCoursePageSection,
    linkToBAO,
    onSelectLearningFormat
  } = t0;
  const [selectedLearningFormat] = useAtom(learningFormatAtom);
  const [selectedCampusId] = useAtom(selectedCampusAtom);
  const [openedAccordion, setOpenedAccordion] = useState(null);
  let t1;
  if ($[0] !== coursePageSection?.keyedCopy) {
    t1 = coursePageSection?.keyedCopy ?? [];
    $[0] = coursePageSection?.keyedCopy;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const modalityRows = useModalityRows(courseLevel, selectedLearningFormat, campuses, selectedCampusId, campusCoursePrices, t1, campusLocationCoursePageSection);
  const isLearnMoreOpen = openedAccordion === LEARN_MORE_VALUE;
  let t2;
  if ($[2] !== modalityRows) {
    t2 = modalityRows.some(_temp2);
    $[2] = modalityRows;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const showBestForRow = t2;
  const maxQuickFactCount = modalityRows.reduce(_temp3, 0);
  const tileRows = 2 + (showBestForRow ? 1 : 0) + maxQuickFactCount;
  let t3;
  if ($[4] !== coursePageSection?.keyedCopy) {
    t3 = coursePageSection?.keyedCopy ?? [];
    $[4] = coursePageSection?.keyedCopy;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const keyedCopy = t3;
  let t4;
  if ($[6] !== courseLevel?.slug || $[7] !== isLearnMoreOpen || $[8] !== keyedCopy || $[9] !== linkToBAO || $[10] !== maxQuickFactCount || $[11] !== modalityRows || $[12] !== onSelectLearningFormat || $[13] !== showBestForRow) {
    t4 = () => modalityRows.map(t5 => {
      const {
        modality,
        modalityOffering,
        priceLines,
        quickFacts,
        isSelectDisabled,
        isSelected
      } = t5;
      return {
        value: modality._id,
        disabled: isSelectDisabled,
        body: <SelectableTile isSelected={isSelected} disabled={isSelectDisabled}><StickyFormatChrome>{isStuck => <><SelectableTile.Content><LearningFormatHeader modality={modality} modalityOffering={modalityOffering} priceLines={priceLines} isCompact={isStuck} /></SelectableTile.Content><SelectableTile.SelectButton isSelected={isSelected} disabled={isSelectDisabled} onClick={() => {
                sendDiscoveryEventBeacon("course_modality_selected", {
                  course_level: courseLevel?.slug.current ?? "",
                  modality: modality.slug.current,
                  auto_select: false
                });
                if (isBeastAcademyModality(modality)) {
                  linkToBAO();
                }
                onSelectLearningFormat(modality);
              }} /></>}</StickyFormatChrome><SelectableTile.Content className={isLearnMoreOpen ? styles.formatWhyChoose : undefined}><LearningFormatWhyChoose modality={modality} keyedCopy={keyedCopy} />{modality.modalityFootnote && <Typography variant="bodySmall" className={styles.modalityFootnote}>{modality.modalityFootnote}</Typography>}</SelectableTile.Content>{showBestForRow ? <LearningFormatDetailRow expanded={isLearnMoreOpen}><LearningFormatBestFor modality={modality} keyedCopy={keyedCopy} /></LearningFormatDetailRow> : null}{Array.from({
            length: maxQuickFactCount
          }, (_, factIndex) => {
            const fact = quickFacts[factIndex];
            return <LearningFormatDetailRow key={fact?._key ?? `quick-fact-${factIndex}`} expanded={isLearnMoreOpen}>{fact ? <LearningFormatQuickFact fact={fact} /> : null}</LearningFormatDetailRow>;
          })}</SelectableTile>
      };
    });
    $[6] = courseLevel?.slug;
    $[7] = isLearnMoreOpen;
    $[8] = keyedCopy;
    $[9] = linkToBAO;
    $[10] = maxQuickFactCount;
    $[11] = modalityRows;
    $[12] = onSelectLearningFormat;
    $[13] = showBestForRow;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  courseLevel?.slug;
  let t5;
  if ($[15] !== t4) {
    t5 = t4();
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const options = t5;
  let t6;
  if ($[17] !== coursePageSection?.title) {
    t6 = coursePageSection?.title?.trim() || FALLBACK_TITLE;
    $[17] = coursePageSection?.title;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const title = t6;
  let t7;
  if ($[19] !== coursePageSection?.body) {
    t7 = coursePageSection?.body?.trim() || FALLBACK_SUBTITLE;
    $[19] = coursePageSection?.body;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const subtitle = t7;
  let t8;
  if ($[21] !== keyedCopy) {
    t8 = getKeyedCopyText(keyedCopy, LEARN_MORE_KEY) || FALLBACK_LEARN_MORE_TITLE;
    $[21] = keyedCopy;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const learnMoreText = t8;
  let t9;
  if ($[23] !== keyedCopy) {
    t9 = getKeyedCopyText(keyedCopy, HIDE_DETAILS_KEY) || FALLBACK_HIDE_DETAILS_TEXT;
    $[23] = keyedCopy;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const hideDetailsText = t9;
  let t10;
  if ($[25] !== keyedCopy) {
    t10 = getKeyedCopyText(keyedCopy, STEP_TAG_KEY) || FALLBACK_STEP_TAG;
    $[25] = keyedCopy;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const stepTag = t10;
  if (!options.length) {
    return null;
  }
  let t11;
  if ($[27] !== courseLevel?.slug || $[28] !== linkToBAO || $[29] !== modalityRows || $[30] !== onSelectLearningFormat) {
    t11 = value => {
      const modality_0 = modalityRows.find(row_1 => row_1.modality._id === value)?.modality;
      if (!modality_0) {
        return;
      }
      sendDiscoveryEventBeacon("course_modality_selected", {
        course_level: courseLevel?.slug.current ?? "",
        modality: modality_0.slug.current,
        auto_select: false
      });
      if (isBeastAcademyModality(modality_0)) {
        linkToBAO();
      }
      onSelectLearningFormat(modality_0);
    };
    $[27] = courseLevel?.slug;
    $[28] = linkToBAO;
    $[29] = modalityRows;
    $[30] = onSelectLearningFormat;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== courseLevel?.slug || $[33] !== modalityRows || $[34] !== openedAccordion) {
    t12 = value_0 => {
      if (didOpenLearnMore(value_0, openedAccordion, LEARN_MORE_VALUE)) {
        const modalitySlug = getSelectedModalitySlug(modalityRows);
        if (modalitySlug) {
          sendDiscoveryEventBeacon("course_modality_details_viewed", {
            course_level: courseLevel?.slug.current ?? "",
            modality: modalitySlug
          });
        }
      }
      setOpenedAccordion(value_0);
    };
    $[32] = courseLevel?.slug;
    $[33] = modalityRows;
    $[34] = openedAccordion;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const t13 = options.length === 1 && styles.accordionRootHalf;
  let t14;
  if ($[36] !== t13) {
    t14 = cn(styles.accordionRoot, t13);
    $[36] = t13;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== t14) {
    t15 = {
      root: t14,
      item: styles.accordionItem,
      control: styles.accordionControl,
      label: styles.accordionLabel
    };
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  const t16 = isLearnMoreOpen ? hideDetailsText : learnMoreText;
  let t17;
  if ($[40] !== t16) {
    t17 = <Typography variant="buttonMd" as="span">{t16}</Typography>;
    $[40] = t16;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== isLearnMoreOpen) {
    t18 = isLearnMoreOpen ? <Minus size={24} strokeWidth={1.5} aria-hidden={true} /> : <Plus size={24} strokeWidth={1.5} aria-hidden={true} />;
    $[42] = isLearnMoreOpen;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== t17 || $[45] !== t18) {
    t19 = <Accordion.Item value={LEARN_MORE_VALUE}><Accordion.Control><span className={styles.accordionControlInner}>{t17}{t18}</span></Accordion.Control></Accordion.Item>;
    $[44] = t17;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== openedAccordion || $[48] !== t12 || $[49] !== t15 || $[50] !== t19) {
    t20 = <Accordion variant="unstyled" value={openedAccordion} onChange={t12} chevron={null} classNames={t15}>{t19}</Accordion>;
    $[47] = openedAccordion;
    $[48] = t12;
    $[49] = t15;
    $[50] = t19;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== options || $[53] !== selectedLearningFormat || $[54] !== stepTag || $[55] !== subtitle || $[56] !== t11 || $[57] !== t20 || $[58] !== tileRows || $[59] !== title) {
    t21 = <CourseLevelSection id="formats" title={title} subtitle={subtitle} stepTag={stepTag} options={options} tileRows={tileRows} defaultValue={selectedLearningFormat} onSelect={t11} footer={t20} />;
    $[52] = options;
    $[53] = selectedLearningFormat;
    $[54] = stepTag;
    $[55] = subtitle;
    $[56] = t11;
    $[57] = t20;
    $[58] = tileRows;
    $[59] = title;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  return t21;
}

/**
 * Learning formats (modalities) for a course level.
 * Desktop (1024px+): multi-column subgrid with a shared Learn more accordion.
 * Tablet/phone (<1024px): single column; each option has its own Learn more
 * accordion. On phone (<768px), whyChoose also moves into that accordion.
 *
 * Modality preference persists in sessionStorage for the tab session. URL
 * `?modality=` wins over the session value. Clearing a campus-disabled
 * selection does not clear the session slug (so later courses can still use it).
 */
function _temp3(max, row_0) {
  return Math.max(max, row_0.quickFacts.length);
}
function _temp2(row) {
  return (row.modality.bestFor?.length ?? 0) > 0;
}
export function LearningFormat(props) {
  const $ = _c(49);
  let t0;
  if ($[0] !== props.courseLevel || $[1] !== props.initialModalitySlug) {
    t0 = getModalityIdBySlug(props.courseLevel, props.initialModalitySlug);
    $[0] = props.courseLevel;
    $[1] = props.initialModalitySlug;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const initialLearningFormatId = t0;
  let t1;
  if ($[3] !== props.initialSelectedCampusId) {
    t1 = [selectedCampusAtom, props.initialSelectedCampusId];
    $[3] = props.initialSelectedCampusId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== initialLearningFormatId) {
    t2 = [learningFormatAtom, initialLearningFormatId];
    $[5] = initialLearningFormatId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [campusResolutionSettledAtom, true];
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2) {
    t4 = [t1, t2, t3];
    $[8] = t1;
    $[9] = t2;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  useHydrateAtoms(t4);
  const [selectedLearningFormat, setSelectedLearningFormat] = useAtom(learningFormatAtom);
  const [sessionModalitySlug, setSessionModalitySlug] = useAtom(selectedModalitySlugAtom);
  const [selectedCampusId] = useAtom(selectedCampusAtom);
  const [campusResolutionSettled] = useAtom(campusResolutionSettledAtom);
  const keyedCopy = props.coursePageSection?.keyedCopy ?? EMPTY_KEYED_COPY;
  const courseId = props.courseLevel?._id ?? null;
  const appliedCourseIdRef = useRef(null);
  const appliedUrlSlugRef = useRef(null);
  const formatAutoScrollAllowedRef = useRef(initialLearningFormatId == null);
  const didFormatAutoScrollRef = useRef(false);
  const modalityRows = useModalityRows(props.courseLevel, selectedLearningFormat, props.campuses, selectedCampusId, props.campusCoursePrices, keyedCopy, props.campusLocationCoursePageSection);
  let t5;
  if ($[11] !== props.courseLevel?.courseOfferings || $[12] !== props.courseLevel?.sequenceType || $[13] !== selectedLearningFormat || $[14] !== setSelectedLearningFormat || $[15] !== setSessionModalitySlug) {
    t5 = modality => {
      if (selectedLearningFormat === modality._id) {
        return;
      }
      setSelectedLearningFormat(modality._id);
      setSessionModalitySlug(modality.slug.current);
      if (isBeastAcademyModality(modality)) {
        formatAutoScrollAllowedRef.current = false;
        return;
      }
      const shouldScroll = shouldAutoScrollOnSelection({
        allowAutoScroll: formatAutoScrollAllowedRef.current,
        alreadyScrolled: didFormatAutoScrollRef.current,
        previousSelectedId: selectedLearningFormat,
        nextSelectedId: modality._id
      });
      formatAutoScrollAllowedRef.current = false;
      if (!shouldScroll) {
        return;
      }
      didFormatAutoScrollRef.current = true;
      const target = getFormatAutoScrollTarget({
        sequenceType: props.courseLevel?.sequenceType,
        courseOfferings: props.courseLevel?.courseOfferings ?? [],
        selectedModalityId: modality._id
      });
      scrollToCourseSection(target);
    };
    $[11] = props.courseLevel?.courseOfferings;
    $[12] = props.courseLevel?.sequenceType;
    $[13] = selectedLearningFormat;
    $[14] = setSelectedLearningFormat;
    $[15] = setSessionModalitySlug;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  props.courseLevel?.courseOfferings;
  props.courseLevel?.sequenceType;
  const onSelectLearningFormat = t5;
  let t6;
  let t7;
  if ($[17] !== courseId || $[18] !== props.courseLevel || $[19] !== props.initialModalitySlug || $[20] !== sessionModalitySlug || $[21] !== setSelectedLearningFormat || $[22] !== setSessionModalitySlug) {
    t6 = () => {
      const previousCourseId = appliedCourseIdRef.current;
      const urlSlug = props.initialModalitySlug ?? null;
      const shouldApply = shouldApplyUrlPreference({
        previousCourseId,
        courseId,
        urlSlug,
        appliedUrlSlug: appliedUrlSlugRef.current
      });
      appliedCourseIdRef.current = courseId;
      if (!shouldApply) {
        return;
      }
      if (previousCourseId !== courseId) {
        didFormatAutoScrollRef.current = false;
        formatAutoScrollAllowedRef.current = true;
      }
      const trimmedUrl = urlSlug?.trim() || null;
      appliedUrlSlugRef.current = trimmedUrl;
      if (trimmedUrl) {
        setSessionModalitySlug(trimmedUrl);
      }
      const preferredSlug = resolvePreferredModalitySlug(trimmedUrl, sessionModalitySlug);
      const preferredId = getModalityIdBySlug(props.courseLevel, preferredSlug);
      setSelectedLearningFormat(preferredId);
      if (preferredId) {
        formatAutoScrollAllowedRef.current = false;
      }
    };
    t7 = [courseId, props.courseLevel, props.initialModalitySlug, sessionModalitySlug, setSelectedLearningFormat, setSessionModalitySlug];
    $[17] = courseId;
    $[18] = props.courseLevel;
    $[19] = props.initialModalitySlug;
    $[20] = sessionModalitySlug;
    $[21] = setSelectedLearningFormat;
    $[22] = setSessionModalitySlug;
    $[23] = t6;
    $[24] = t7;
  } else {
    t6 = $[23];
    t7 = $[24];
  }
  useEffect(t6, t7);
  let t8;
  if ($[25] !== modalityRows[0] || $[26] !== modalityRows.length || $[27] !== selectedLearningFormat || $[28] !== setSelectedLearningFormat || $[29] !== setSessionModalitySlug) {
    t8 = () => {
      if (modalityRows.length !== 1) {
        return;
      }
      const row = modalityRows[0];
      if (!row || row.isSelectDisabled) {
        return;
      }
      formatAutoScrollAllowedRef.current = false;
      if (selectedLearningFormat === row.modality._id) {
        return;
      }
      setSelectedLearningFormat(row.modality._id);
      setSessionModalitySlug(row.modality.slug.current);
    };
    $[25] = modalityRows[0];
    $[26] = modalityRows.length;
    $[27] = selectedLearningFormat;
    $[28] = setSelectedLearningFormat;
    $[29] = setSessionModalitySlug;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== modalityRows || $[32] !== selectedLearningFormat || $[33] !== setSelectedLearningFormat || $[34] !== setSessionModalitySlug) {
    t9 = [modalityRows, selectedLearningFormat, setSelectedLearningFormat, setSessionModalitySlug];
    $[31] = modalityRows;
    $[32] = selectedLearningFormat;
    $[33] = setSelectedLearningFormat;
    $[34] = setSessionModalitySlug;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[36] !== campusResolutionSettled || $[37] !== modalityRows || $[38] !== setSelectedLearningFormat) {
    t10 = () => {
      if (!campusResolutionSettled) {
        return;
      }
      const selectedRow = modalityRows.find(_temp4);
      if (selectedRow?.isSelectDisabled) {
        setSelectedLearningFormat(null);
        if (!didFormatAutoScrollRef.current) {
          formatAutoScrollAllowedRef.current = true;
        }
      }
    };
    t11 = [campusResolutionSettled, modalityRows, setSelectedLearningFormat];
    $[36] = campusResolutionSettled;
    $[37] = modalityRows;
    $[38] = setSelectedLearningFormat;
    $[39] = t10;
    $[40] = t11;
  } else {
    t10 = $[39];
    t11 = $[40];
  }
  useEffect(t10, t11);
  const isStacked = useIsStackedLearningFormat();
  const linkToBAO = useLinkToBAO();
  if (isStacked) {
    let t12;
    if ($[41] !== linkToBAO || $[42] !== onSelectLearningFormat || $[43] !== props) {
      t12 = <LearningFormatMobile {...props} linkToBAO={linkToBAO} onSelectLearningFormat={onSelectLearningFormat} />;
      $[41] = linkToBAO;
      $[42] = onSelectLearningFormat;
      $[43] = props;
      $[44] = t12;
    } else {
      t12 = $[44];
    }
    return t12;
  }
  let t12;
  if ($[45] !== linkToBAO || $[46] !== onSelectLearningFormat || $[47] !== props) {
    t12 = <LearningFormatDesktop {...props} linkToBAO={linkToBAO} onSelectLearningFormat={onSelectLearningFormat} />;
    $[45] = linkToBAO;
    $[46] = onSelectLearningFormat;
    $[47] = props;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  return t12;
}
function _temp4(row_0) {
  return row_0.isSelected;
}